import { ActionIcon, Anchor, Badge, Button, Card, Group, Menu, Modal, Progress, Stack, Table, Text, TextInput, Tooltip } from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ErrorBox, notifyError, notifyOk } from "../components/Errors";
import { JobsTable } from "../components/Jobs";
import { PageTitle } from "../components/Layout";
import { del, download, formatBytes, formatDate, get, patch, uploadFile, type Job, type ProjectDetail as PD, type StoredFile } from "../lib/api";
import { useMe } from "../lib/hooks";
import { Crumbs, ModuleBadge } from "../components/Modules";
import { moduleOfProduct, modulePath } from "../lib/modules";
import { NegfWorkspace } from "./negf/NegfWorkspace";
import { PropertiesWorkspace } from "./properties/PropertiesWorkspace";
import { DftWorkspace } from "./dft/DftWorkspace";
import { SkfitterWorkspace } from "./skfitter/SkfitterWorkspace";
import { TransportWorkspace } from "./TransportWorkspace";
import { WannierWorkspace } from "./wannier/WannierWorkspace";

export function ProjectDetail() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ["project", id], queryFn: () => get<PD>(`/projects/${id}`),
    refetchInterval: (s) => (s.state.data?.jobs.some((j) => j.status === "queued" || j.status === "running") ? 3000 : false) });
  const [renaming, setRenaming] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["project", id] });
    qc.invalidateQueries({ queryKey: ["projects"] });
    qc.invalidateQueries({ queryKey: ["me"] });
  };
  const remove = useMutation({
    mutationFn: () => del(`/projects/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      notifyOk("Project deleted.");
      nav("/projects");
    },
    onError: (e) => notifyError(e),
  });
  if (q.error) return <ErrorBox error={q.error} />;
  const p = q.data;
  if (!p) return null;
  const mod = moduleOfProduct(p.product);

  return (
    <Stack>
      <div>
        <Crumbs items={[{ label: "Home", to: "/" },
          ...(mod ? [{ label: mod.name, to: modulePath(mod.slug) }] : [{ label: "Projects", to: "/projects" }]), { label: p.name }]} />
        <PageTitle title={p.name}>
          <ModuleBadge product={p.product} size="lg" />
        <Button variant="default" onClick={() => download(`/projects/${p.id}/archive`, `${p.name}.zip`).catch(notifyError)}
          disabled={!p.files.some((f) => f.status === "ready")}>
          Download as zip
        </Button>
        <Menu position="bottom-end">
          <Menu.Target><Button variant="default">More</Button></Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={() => setRenaming(true)}>Rename</Menu.Item>
            <Menu.Item color="red" onClick={() => setConfirming(true)}>Delete project</Menu.Item>
          </Menu.Dropdown>
        </Menu>
        </PageTitle>
      </div>
      <RenameModal project={p} opened={renaming} onClose={() => setRenaming(false)} onDone={refresh} />
      <Modal opened={confirming} onClose={() => setConfirming(false)} title={`Delete "${p.name}"?`} centered>
        <Text size="sm">This deletes the project and its {p.files.length} file(s) for good. Calculations stay in your job list.</Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setConfirming(false)}>Keep</Button>
          <Button color="red" loading={remove.isPending} onClick={() => remove.mutate()}>Delete</Button>
        </Group>
      </Modal>

      {p.product === "transport" && <TransportWorkspace project={p} />}
      {p.product === "skfitter" && <SkfitterWorkspace project={p} />}
      {p.product === "negf" && <NegfWorkspace project={p} />}
      {p.product === "dft" && <DftWorkspace project={p} />}
      {p.product === "properties" && <PropertiesWorkspace project={p} />}
      {p.product === "wannier" && <WannierWorkspace project={p} />}

      <Card withBorder>
        <Text fw={600} mb="xs">Files</Text>
        <Uploader projectId={p.id} onDone={refresh} />
        <FilesTable files={p.files} onChange={refresh} />
      </Card>

      <Card withBorder>
        <Text fw={600} mb="xs">Calculations</Text>
        <JobsTable jobs={p.jobs as Job[]} showProject={false} />
      </Card>
    </Stack>
  );
}

function RenameModal({ project, opened, onClose, onDone }: { project: PD; opened: boolean; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(project.name);
  const m = useMutation({ mutationFn: () => patch(`/projects/${project.id}`, { name }), onSuccess: () => { onDone(); onClose(); } });
  return (
    <Modal opened={opened} onClose={onClose} title="Rename project" centered>
      <Stack>
        <TextInput value={name} onChange={(e) => setName(e.currentTarget.value)} maxLength={120} data-autofocus />
        <ErrorBox error={m.error} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={() => m.mutate()} loading={m.isPending} disabled={!name.trim()}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function Uploader({ projectId, onDone }: { projectId: string; onDone: () => void }) {
  const { data: me } = useMe();
  const [items, setItems] = useState<{ name: string; progress: number }[]>([]);
  const [error, setError] = useState<unknown>(null);
  const maxMb = Number(me?.limits["storage.max_upload_mb"] ?? 0);

  const onDrop = async (files: File[]) => {
    setError(null);
    for (const f of files) {
      setItems((it) => [...it, { name: f.name, progress: 0 }]);
      try {
        await uploadFile(projectId, f, (fr) => setItems((it) => it.map((x) => (x.name === f.name ? { ...x, progress: fr } : x))));
        notifyOk(`${f.name} uploaded.`);
      } catch (e) {
        setError(e);
      } finally {
        setItems((it) => it.filter((x) => x.name !== f.name));
        onDone();
      }
    }
  };

  return (
    <Stack gap="xs" mb="sm">
      <Dropzone onDrop={onDrop} multiple>
        <Group justify="center" mih={70}>
          <Stack gap={0} align="center">
            <Text>Drop files here, or click to choose</Text>
            <Text size="xs" c="dimmed">Up to {maxMb >= 1024 ? `${maxMb / 1024} GB` : `${maxMb} MB`} per file on your plan</Text>
          </Stack>
        </Group>
      </Dropzone>
      {items.map((it) => (
        <Group key={it.name} gap="xs" wrap="nowrap">
          <Text size="sm" w={200} truncate>{it.name}</Text>
          <Progress value={it.progress * 100} style={{ flex: 1 }} aria-label={`Uploading ${it.name}`} />
        </Group>
      ))}
      <ErrorBox error={error} />
    </Stack>
  );
}

function FilesTable({ files, onChange }: { files: StoredFile[]; onChange: () => void }) {
  const remove = useMutation({ mutationFn: (id: string) => del(`/files/${id}`), onSuccess: onChange, onError: (e) => notifyError(e) });
  if (!files.length) return <Text c="dimmed" size="sm">No files yet.</Text>;
  return (
    <Table.ScrollContainer minWidth={600}>
      <Table verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr><Table.Th>Name</Table.Th><Table.Th>Type</Table.Th><Table.Th>Size</Table.Th><Table.Th>Added</Table.Th>
            <Table.Th>Deleted after</Table.Th><Table.Th /></Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {files.map((f) => (
            <Table.Tr key={f.id}>
              <Table.Td>
                {f.status === "ready"
                  ? <Anchor component="button" onClick={() => download(`/files/${f.id}/download`, f.name).catch(notifyError)}>{f.name}</Anchor>
                  : <Text c="dimmed" size="sm">{f.name} (upload not finished)</Text>}
              </Table.Td>
              <Table.Td><Badge variant="dot" color={f.kind === "result" ? "teal" : f.kind === "copy" ? "grape" : "blue"}>
                {f.kind === "result" ? "Result" : f.kind === "copy" ? "Copy" : "Upload"}</Badge></Table.Td>
              <Table.Td>{formatBytes(f.size)}</Table.Td>
              <Table.Td>{formatDate(f.created_at)}</Table.Td>
              <Table.Td>{formatDate(f.expires_at)}</Table.Td>
              <Table.Td>
                <Tooltip label="Delete file">
                  <ActionIcon variant="subtle" color="red" aria-label={`Delete ${f.name}`} onClick={() => remove.mutate(f.id)}>✕</ActionIcon>
                </Tooltip>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
